<template>
  <section class="mobile-images">
    <!-- 홍보 이미지들 -->
    <template v-for="(image, index) in images" :key="index">
      <div class="image-wrapper">
        <img
          :src="image"
          :alt="`홍보 이미지 ${index + 1}`"
          loading="lazy"
        />
      </div>

      <!-- home1 뒤 스크롤 힌트 -->
      <div v-if="index === 0" class="scroll-hint">
        <svg
          v-for="n in 5"
          :key="n"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="3"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </div>

      <!-- home2 뒤 HUG 보증 문구 -->
      <div v-if="index === 1" class="hug-banner">
        <span class="hug-chip">
          <svg class="hug-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 2l8 4v6c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6l8-4z"/>
            <polyline points="9 12 11 14 15 10"/>
          </svg>
          HUG 보증
        </span>
        <h3 class="hug-headline">
          100% 안전한<br />
          내 집 마련
        </h3>
        <p class="hug-sub">주택도시보증공사가 보증하는 안심 주거</p>
      </div>

      <!-- home3 뒤 사업개요 -->
      <div v-if="index === 2" class="overview-section">
        <p class="section-eyebrow">OVERVIEW</p>
        <h3 class="section-heading">사업개요</h3>

        <table class="info-table">
          <tbody>
            <tr>
              <th>위치</th>
              <td>오산시 부산동 614-12번지 일원</td>
            </tr>
            <tr>
              <th>규모</th>
              <td>B3~29F, 15개동, 1,606세대</td>
            </tr>
            <tr>
              <th>대지면적</th>
              <td>72,567.00㎡ (21,951.52평)</td>
            </tr>
            <tr>
              <th>연면적</th>
              <td>226,347.9876㎡ (68,470.27평)</td>
            </tr>
            <tr>
              <th>건폐율 / 용적률</th>
              <td>12.38% / 199.26%</td>
            </tr>
            <tr>
              <th>주차대수</th>
              <td>2,260대 (세대당 1.4대)</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 사업개요 뒤 입지환경 -->
      <div v-if="index === 2" class="location-section">
        <h3 class="section-heading">입지환경</h3>
        <div class="location-image">
          <img :src="locationImage" alt="입지환경" />
        </div>

        <table class="info-table location-table">
          <tbody>
            <tr>
              <th>교통</th>
              <td>
                <strong class="td-headline">사통팔달 쾌속 광역교통망</strong>
                <ul>
                  <li>경부고속도로(오산IC), 용서고속도로 쾌속 진입</li>
                  <li>오산역 약 1.7KM 거리 위치, 1번 국도 인접</li>
                </ul>
              </td>
            </tr>
            <tr>
              <th>교육</th>
              <td>
                <strong class="td-headline">반경 1km 內 초·중·고 위치</strong>
                <ul>
                  <li>도보권역 내 다온초, 원당초 위치</li>
                  <li>중심상업지 학원가 이용 편리</li>
                </ul>
              </td>
            </tr>
            <tr>
              <th>편의</th>
              <td>
                <strong class="td-headline">내일이 기대되는 미래비전</strong>
                <ul>
                  <li>운암뜰 AI시티 도시개발 인접</li>
                  <li>동탄트램 운암역 인접 예정</li>
                </ul>
              </td>
            </tr>
            <tr>
              <th>환경</th>
              <td>
                <strong class="td-headline">풍부하고 다양한 생활 인프라</strong>
                <ul>
                  <li>오산시청, 오산경찰서 등 관공서 인접</li>
                  <li>롯데마트, 및 시청 앞 중심상업지역 인접</li>
                </ul>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 입지환경 뒤 단지배치도 -->
      <div v-if="index === 2" class="site-plan-section">
        <h3 class="section-heading">단지배치도</h3>
        <div class="site-plan-image">
          <img :src="sitePlanImage" alt="단지배치도" />
        </div>
      </div>

      <!-- 단지배치도 뒤 동호수배치도 -->
      <div v-if="index === 2" class="unit-plan-section">
        <h3 class="section-heading">동호수배치도</h3>
        <div class="unit-plan-images">
          <img :src="unitPlanImage1" alt="동호수배치도 1" />
          <img :src="unitPlanImage2" alt="동호수배치도 2" />
        </div>
      </div>

      <!-- 동호수배치도 뒤 세대안내 -->
      <div v-if="index === 2" class="unit-guide-section">
        <h3 class="section-heading">세대안내</h3>
        <div class="unit-tabs">
          <button
            v-for="type in unitTypes"
            :key="type.key"
            type="button"
            class="unit-tab"
            :class="{ active: activeUnitType === type.key }"
            @click="activeUnitType = type.key"
          >
            {{ type.key }}
          </button>
        </div>
        <div class="unit-image">
          <img :src="activeUnitImage" :alt="activeUnitType + ' 타입'" />
        </div>
      </div>

      <!-- 세대안내 뒤 프리미엄 6 (모바일 컴팩트) -->
      <div v-if="index === 2" class="premium-compact-section">
        <h3 class="section-heading">PREMIUM</h3>
        <ul class="premium-list">
          <li
            v-for="item in premiumItems"
            :key="item.number"
            class="premium-item"
          >
            <div class="premium-badge">
              <div class="badge-icon" v-html="item.icon"></div>
              <div class="badge-number">{{ String(item.number).padStart(2, '0') }}</div>
            </div>
            <div class="premium-text">
              <h4 class="premium-title">{{ item.title }}</h4>
              <p class="premium-desc">{{ item.desc }}</p>
            </div>
          </li>
        </ul>
      </div>

      <!-- 프리미엄 뒤 커뮤니티 -->
      <div v-if="index === 2" class="community-section">
        <h3 class="section-heading">커뮤니티</h3>
        <div class="community-grid">
          <article
            v-for="item in communityItems"
            :key="item.name"
            class="community-card"
          >
            <div class="card-image">
              <img :src="item.image" :alt="item.name" loading="lazy" />
            </div>
            <h4 class="card-name">{{ item.name }}</h4>
          </article>
        </div>
      </div>
    </template>

    <!-- 하단 관심고객등록 폼 -->
    <div class="inline-form-section">
      <div class="form-header">
        <span class="form-badge">CONTACT</span>
        <h3 class="form-title">관심고객등록</h3>
        <p class="form-desc">연락처를 남겨주시면 담당자가 빠르게 연락드립니다</p>
      </div>

      <form @submit.prevent="submitForm" class="register-form">
        <div class="form-group">
          <input
            type="text"
            v-model="form.name"
            placeholder="이름"
            required
            :disabled="isSubmitting"
          />
        </div>
        <div class="form-group">
          <div class="phone-inputs">
            <input
              ref="phone1"
              type="tel"
              inputmode="numeric"
              v-model="form.phone1"
              placeholder="010"
              maxlength="3"
              required
              :disabled="isSubmitting"
              @input="onPhoneInput('phone1', 3, 'phone2')"
            />
            <span class="phone-dash">-</span>
            <input
              ref="phone2"
              type="tel"
              inputmode="numeric"
              v-model="form.phone2"
              placeholder="0000"
              maxlength="4"
              required
              :disabled="isSubmitting"
              @input="onPhoneInput('phone2', 4, 'phone3')"
            />
            <span class="phone-dash">-</span>
            <input
              ref="phone3"
              type="tel"
              inputmode="numeric"
              v-model="form.phone3"
              placeholder="0000"
              maxlength="4"
              required
              :disabled="isSubmitting"
              @input="onPhoneInput('phone3', 4, null)"
            />
          </div>
        </div>
        <div class="form-group">
          <label class="field-label">방문예약 희망일</label>
          <div class="visit-field">
            <input
              type="datetime-local"
              v-model="form.visitDate"
              :disabled="isSubmitting"
            />
            <span v-if="!form.visitDate" class="visit-placeholder">희망 일시 선택</span>
          </div>
        </div>
        <label class="checkbox-label">
          <input type="checkbox" v-model="form.agree" required :disabled="isSubmitting" />
          <span class="checkmark"></span>
          <span>개인정보 수집 및 이용 동의</span>
        </label>
        <button type="submit" class="submit-btn" :disabled="isSubmitting">
          {{ isSubmitting ? '등록 중...' : '상담 신청하기' }}
        </button>
      </form>
    </div>
  </section>
</template>

<script>
import { saveConsultation } from '@/firebase/consultationService'

export default {
  name: 'MobileImageSection',
  data() {
    return {
      images: [],
      locationImage: require('@/assets/premium/location.jpg'),
      sitePlanImage: require('@/assets/단지배치도.png'),
      unitPlanImage1: require('@/assets/동호배치도1.png'),
      unitPlanImage2: require('@/assets/동호배치도2.png'),
      unitTypes: [
        { key: '59A', image: require('@/assets/59A.webp') },
        { key: '59B', image: require('@/assets/59B.webp') },
        { key: '84A', image: require('@/assets/84A.webp') },
        { key: '84B', image: require('@/assets/84B.webp') }
      ],
      activeUnitType: '59A',
      premiumItems: [
        {
          number: 1,
          title: '쿼드러플 역세권',
          desc: '1호선, GTX-C(예정), 동탄트램(예정), 수원발 KTX(추진중)까지 쿼드러플 교통망',
          icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="3" width="14" height="15" rx="2"/><path d="M5 11h14"/><path d="M8 18l-2 3"/><path d="M16 18l2 3"/><circle cx="9" cy="14" r="0.5" fill="currentColor"/><circle cx="15" cy="14" r="0.5" fill="currentColor"/></svg>`
        },
        {
          number: 2,
          title: 'AI산업 직주근접',
          desc: '삼성+하이닉스 반도체 메가벨트 고용 약 300만명, 인접 9개 산업단지 고정수요 100만명',
          icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="6" y="6" width="12" height="12" rx="1"/><rect x="9" y="9" width="6" height="6"/><path d="M9 3v3M12 3v3M15 3v3M9 18v3M12 18v3M15 18v3"/><path d="M3 9h3M3 12h3M3 15h3M18 9h3M18 12h3M18 15h3"/></svg>`
        },
        {
          number: 3,
          title: '미니신도시 대단지',
          desc: '원동지구(운암뜰 AI시티 포함) 약 2만여 세대 내 1,606세대 대단지 아파트',
          icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 21h18"/><path d="M5 21V7l6-3v17"/><path d="M14 21V10h5v11"/></svg>`
        },
        {
          number: 4,
          title: '동탄/오산 생활권',
          desc: '오산역 중심상권 + 동탄2신도시 풍부한 생활인프라, 약 3만평 복합시설 도보권',
          icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M6 8h12l-1 12H7L6 8z"/><path d="M9 8V5a3 3 0 0 1 6 0v3"/></svg>`
        },
        {
          number: 5,
          title: '학세권 / 공세권',
          desc: '도보 통학 가능한 초·중학교 + 단지 옆 약 3,051평 대형 공원',
          icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-5 9 5-9 5-9-5z"/><path d="M7 11v4c0 1.5 2.5 3 5 3s5-1.5 5-3v-4"/><path d="M21 9v4"/></svg>`
        },
        {
          number: 6,
          title: '민간임대',
          desc: '청약통장 없이 주변 시세 반값. 10년 거주 후 분양여부 결정 가능',
          icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="8" cy="15" r="4"/><path d="M10.8 12.2L21 2l-2 5 2 2-3 1 1 3-3-1-2 2"/></svg>`
        }
      ],
      communityItems: [
        { name: '물놀이터', image: require('@/assets/mobilecommunity/swim.jpg') },
        { name: '세대창고', image: require('@/assets/mobilecommunity/창고.jpg') },
        { name: '피트니스', image: require('@/assets/mobilecommunity/gym.jpg') },
        { name: '사우나', image: require('@/assets/mobilecommunity/sauna.jpg') },
        { name: '실내 골프 연습장', image: require('@/assets/mobilecommunity/golf.jpg') },
        { name: '키즈카페', image: 'https://loremflickr.com/800/600/kids,playground,cafe' }
      ],
      isSubmitting: false,
      form: {
        name: '',
        phone1: '010',
        phone2: '',
        phone3: '',
        visitDate: '',
        agree: false
      }
    }
  },
  computed: {
    activeUnitImage() {
      const found = this.unitTypes.find(t => t.key === this.activeUnitType)
      return found ? found.image : null
    }
  },
  created() {
    const exts = ['jpg', 'gif', 'png']
    for (let i = 1; i <= 20; i++) {
      for (const ext of exts) {
        try {
          const img = require(`@/assets/mobile/home${i}.${ext}`)
          this.images.push(img)
          break
        } catch (e) {
          // 다음 확장자 시도
        }
      }
    }
  },
  methods: {
    onPhoneInput(field, maxLen, nextRef) {
      this.form[field] = this.form[field].replace(/[^0-9]/g, '')
      if (this.form[field].length >= maxLen && nextRef) {
        this.$refs[nextRef].focus()
      }
    },
    async submitForm() {
      if (!this.form.agree) {
        alert('개인정보 수집 및 이용에 동의해주세요.')
        return
      }

      this.isSubmitting = true

      try {
        const result = await saveConsultation({
          name: this.form.name,
          phone: `${this.form.phone1}-${this.form.phone2}-${this.form.phone3}`,
          visitDate: this.form.visitDate
        }, 'mobile-home')

        if (result.success) {
          alert(`${this.form.name}님, 상담 신청이 완료되었습니다.\n빠른 시일 내에 연락드리겠습니다.`)
          this.form = { name: '', phone1: '010', phone2: '', phone3: '', visitDate: '', agree: false }
        } else {
          alert('오류가 발생했습니다. 다시 시도해주세요.')
        }
      } catch (error) {
        console.error('등록 오류:', error)
        alert('오류가 발생했습니다. 다시 시도해주세요.')
      } finally {
        this.isSubmitting = false
      }
    }
  }
}
</script>

<style scoped>
.mobile-images {
  width: 100%;
}

.image-wrapper {
  width: 100%;
  line-height: 0;
}

.image-wrapper img {
  width: 100%;
  height: auto;
  display: block;
}

/* 스크롤 힌트 (화살표 5개) */
.scroll-hint {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 14px;
  padding: 18px 20px;
  background: #fff;
}

.scroll-hint svg {
  width: 24px;
  height: 24px;
  color: var(--main-color1);
  opacity: 0.25;
  animation: scroll-wave 1.4s ease-in-out infinite;
}

.scroll-hint svg:nth-child(1) { animation-delay: 0s; }
.scroll-hint svg:nth-child(2) { animation-delay: 0.12s; }
.scroll-hint svg:nth-child(3) { animation-delay: 0.24s; }
.scroll-hint svg:nth-child(4) { animation-delay: 0.36s; }
.scroll-hint svg:nth-child(5) { animation-delay: 0.48s; }

@keyframes scroll-wave {
  0%, 100% {
    transform: translateY(0);
    opacity: 0.25;
  }
  50% {
    transform: translateY(6px);
    opacity: 1;
  }
}

/* 입지환경 */
.location-section {
  padding: 40px 20px 48px;
  background: #fff;
}

.section-heading {
  font-size: 20px;
  font-weight: 700;
  color: #1a1a1a;
  text-align: center;
  margin-bottom: 24px;
  letter-spacing: -0.5px;
}

.location-image {
  width: 100%;
  margin-bottom: 28px;
}

.location-image img {
  width: 100%;
  height: auto;
  display: block;
  border-radius: 8px;
}

/* 단지배치도 */
.site-plan-section {
  padding: 40px 20px 48px;
  background: #f8f9fa;
}

.site-plan-image {
  width: 100%;
}

.site-plan-image img {
  width: 100%;
  height: auto;
  display: block;
  border-radius: 8px;
}

/* 동호수배치도 */
.unit-plan-section {
  padding: 40px 20px 48px;
  background: #fff;
}

.unit-plan-images {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.unit-plan-images img {
  width: 100%;
  height: auto;
  display: block;
  border-radius: 8px;
}

/* 프리미엄 (컴팩트) */
.premium-compact-section {
  padding: 40px 20px 48px;
  background: #f8f9fa;
}

.premium-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.premium-item {
  display: flex;
  gap: 14px;
  padding: 14px;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
  align-items: center;
}

.premium-badge {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 64px;
  height: 64px;
  padding: 10px 8px;
  background: linear-gradient(135deg, var(--main-color1) 0%, var(--main-color2) 100%);
  border-radius: 12px;
  color: #fff;
  box-shadow: 0 4px 12px rgba(196, 108, 88, 0.25);
}

.badge-icon {
  width: 26px;
  height: 26px;
  line-height: 0;
}

.badge-icon :deep(svg) {
  width: 100%;
  height: 100%;
  color: #fff;
}

.badge-number {
  font-size: 14px;
  font-weight: 800;
  font-family: 'Georgia', serif;
  letter-spacing: -0.5px;
  line-height: 1;
  opacity: 0.95;
}

.premium-text {
  flex: 1;
  min-width: 0;
}

.premium-title {
  font-size: 15px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 5px;
  letter-spacing: -0.3px;
}

.premium-desc {
  font-size: 12.5px;
  line-height: 1.55;
  color: #666;
  letter-spacing: -0.2px;
  margin: 0;
}

/* 커뮤니티 */
.community-section {
  padding: 40px 20px 48px;
  background: #fff;
}

.community-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px;
}

.community-card {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.06);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}

.community-card:last-child:nth-child(odd) {
  grid-column: span 2;
}

.community-card:active {
  transform: scale(0.98);
}

.card-image {
  width: 100%;
  aspect-ratio: 4 / 3;
  background: #e8e8e8;
  overflow: hidden;
}

.card-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.card-name {
  padding: 12px 14px 14px;
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
  letter-spacing: -0.3px;
  text-align: center;
  margin: 0;
}

/* 세대안내 (탭) */
.unit-guide-section {
  padding: 40px 20px 48px;
  background: #f8f9fa;
}

.unit-tabs {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0;
  margin-bottom: 24px;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.unit-tab {
  padding: 14px 8px;
  font-size: 15px;
  font-weight: 600;
  color: #555;
  background: #fff;
  border: none;
  border-right: 1px solid #eee;
  cursor: pointer;
  font-family: inherit;
  letter-spacing: 0.3px;
  transition: background 0.2s, color 0.2s;
}

.unit-tab:last-child {
  border-right: none;
}

.unit-tab.active {
  background: var(--main-color1);
  color: #fff;
}

.unit-image {
  width: 100%;
}

.unit-image img {
  width: 100%;
  height: auto;
  display: block;
  border-radius: 8px;
}

.location-table .td-headline {
  display: block;
  font-size: 14px;
  font-weight: 700;
  color: var(--main-color1);
  margin-bottom: 8px;
  letter-spacing: -0.3px;
}

.location-table td ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.location-table td li {
  position: relative;
  padding-left: 10px;
  font-size: 13px;
  line-height: 1.6;
  color: #555;
  letter-spacing: -0.2px;
}

.location-table td li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0.7em;
  width: 3px;
  height: 3px;
  background: #aaa;
  border-radius: 50%;
}

/* HUG 배너 */
.hug-banner {
  position: relative;
  padding: 52px 24px 56px;
  text-align: center;
  background: linear-gradient(180deg, #fbf6f1 0%, #f0e6dc 100%);
  overflow: hidden;
}

.hug-banner::before,
.hug-banner::after {
  content: '';
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  width: 40px;
  height: 1px;
  background: var(--main-color1);
  opacity: 0.25;
}

.hug-banner::before {
  top: 22px;
}

.hug-banner::after {
  bottom: 22px;
}

.hug-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px 7px 11px;
  background: #fff;
  border: 1px solid #e7d8ca;
  border-radius: 50px;
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 1.2px;
  color: var(--main-color1);
  margin-bottom: 18px;
  box-shadow: 0 2px 10px rgba(196, 108, 88, 0.08);
}

.hug-icon {
  width: 13px;
  height: 13px;
  color: var(--main-color1);
  flex-shrink: 0;
}

.hug-headline {
  font-size: 24px;
  font-weight: 800;
  color: #2a1e14;
  letter-spacing: -0.8px;
  line-height: 1.35;
  margin: 0 0 10px;
}

.hug-sub {
  margin: 0;
  font-size: 12.5px;
  font-weight: 500;
  color: #8a7666;
  letter-spacing: -0.2px;
}

/* 사업개요 */
.overview-section {
  padding: 40px 20px 48px;
  background: #f8f9fa;
}

.section-eyebrow {
  margin: 0 0 8px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 3px;
  color: var(--main-color1);
  text-align: center;
}

.section-eyebrow + .section-heading {
  margin-bottom: 22px;
}

.info-table {
  width: 100%;
  border-collapse: collapse;
  border-top: 2px solid var(--main-color1);
}

.info-table th,
.info-table td {
  padding: 14px 12px;
  font-size: 13px;
  line-height: 1.6;
  border-bottom: 1px solid #e0e0e0;
  text-align: left;
  vertical-align: top;
}

.info-table th {
  width: 90px;
  font-weight: 600;
  color: #333;
  background: #f0f2f5;
  white-space: nowrap;
}

.info-table td {
  color: #555;
}

/* 하단 인라인 폼 */
.inline-form-section {
  width: 100%;
  padding: 48px 24px 60px;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  position: relative;
  overflow: hidden;
}

.inline-form-section::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(196, 108, 88, 0.15) 0%, transparent 60%);
  animation: pulse-bg 4s ease-in-out infinite;
}

@keyframes pulse-bg {
  0%, 100% { transform: scale(1); opacity: 0.5; }
  50% { transform: scale(1.1); opacity: 1; }
}

.form-header {
  position: relative;
  z-index: 2;
  text-align: center;
  margin-bottom: 28px;
}

.form-badge {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 3px;
  color: var(--main-color1);
  margin-bottom: 14px;
}

.form-title {
  font-size: 26px;
  font-weight: 700;
  color: #fff;
  margin-bottom: 10px;
  letter-spacing: -0.5px;
}

.form-desc {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.6;
}

/* 폼 */
.register-form {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 360px;
  margin: 0 auto;
}

.form-group input {
  width: 100%;
  padding: 14px 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  font-size: 15px;
  font-family: inherit;
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  transition: border-color 0.2s;
}

.form-group input:focus {
  outline: none;
  border-color: var(--main-color1);
  background: rgba(255, 255, 255, 0.15);
}

.form-group input::placeholder {
  color: rgba(255, 255, 255, 0.4);
}

/* 방문예약 일자/시간 */
.field-label {
  display: block;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 6px;
}

.visit-inputs {
  display: flex;
  gap: 8px;
}

.visit-field {
  flex: 1;
  position: relative;
  min-width: 0;
}

.visit-field input {
  width: 100%;
  color: transparent;
}

.visit-field input:valid,
.visit-field input:not([value=""]) {
  color: #fff;
}

.visit-placeholder {
  position: absolute;
  top: 50%;
  left: 16px;
  transform: translateY(-50%);
  font-size: 14px;
  color: rgba(255, 255, 255, 0.4);
  pointer-events: none;
}

.visit-field input::-webkit-calendar-picker-indicator {
  filter: invert(1);
  opacity: 0.5;
}

/* 전화번호 3칸 */
.phone-inputs {
  display: flex;
  align-items: center;
  gap: 6px;
}

.phone-inputs input {
  flex: 1;
  text-align: center;
  min-width: 0;
}

.phone-dash {
  color: rgba(255, 255, 255, 0.4);
  font-size: 16px;
  flex-shrink: 0;
}

/* 체크박스 */
.checkbox-label {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  padding: 8px 0;
}

.checkbox-label input[type="checkbox"] {
  display: none;
}

.checkmark {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  position: relative;
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.checkbox-label input:checked + .checkmark {
  background: var(--main-color1);
  border-color: var(--main-color1);
}

.checkbox-label input:checked + .checkmark::after {
  content: '';
  position: absolute;
  top: 2px;
  left: 6px;
  width: 5px;
  height: 10px;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

/* 제출 버튼 */
.submit-btn {
  width: 100%;
  padding: 16px;
  background: var(--main-color1);
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.3s;
  margin-top: 4px;
}

.submit-btn:active:not(:disabled) {
  background: var(--main-color2);
  transform: scale(0.98);
}

.submit-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}
</style>
